import React, { Component, Fragment } from 'react'
import PropTypes from 'prop-types'
import { Spin } from './common/Ant'
import { H3, H4 } from './common/Headers'
import { createQueryString, parseQueryParams } from '../utils/queryParams'
import { ROLES, SMOOTH_INTERVAL } from '../constants'
import moment from 'moment'
import DateRange from './common/DateRange'
import { hasRole } from '../utils/hasPermission'
import { toTitleCase } from '../utils/textFormatters'
import Select, { Option } from './common/Select'
import PressureMonitorChart from './charts/PressureMonitorChart'

const POLL_INTERVAL_MS = 30000

class PressureMonitorData extends Component {
  static propTypes = {
    updateQueryParams: PropTypes.func.isRequired,
    pressureMonitorData: PropTypes.object.isRequired,
    getPressureMonitorData: PropTypes.func.isRequired,
    isLoading: PropTypes.bool.isRequired,
    pillarId: PropTypes.string.isRequired,
  }

  static defaultProps = {
    pressureMonitorData: {},
  }

  now = moment()

  constructor(props) {
    super(props)

    this.state = {
      now: this.now,
      endValue: this.now,
      startValue: moment(this.now).subtract(1, 'd'),
      smoothInterval: SMOOTH_INTERVAL.RAW,
    }
  }

  componentDidMount() {
    const { getPressureMonitorData, pillarId } = this.props
    const { startValue, endValue } = this.state

    const searchParams = parseQueryParams(global.location)

    let query
    if (searchParams.startTime && searchParams.endTime) {
      query = {
        startTime: searchParams.startTime,
        endTime: searchParams.endTime,
      }
      this.setState({
        startValue: moment(searchParams.startTime * 1000),
        endValue: moment(searchParams.endTime * 1000),
      })
    } else {
      query = {
        startTime: startValue.unix(),
        endTime: endValue.unix(),
      }
    }

    let { smoothInterval } = this.state
    if (searchParams.smoothInterval || searchParams.smooth) {
      smoothInterval = searchParams.smoothInterval
        ? searchParams.smoothInterval
        : !searchParams.smooth || searchParams.smooth === 't'
        ? SMOOTH_INTERVAL.ONE_HOUR
        : SMOOTH_INTERVAL.RAW

      this.setState({ smoothInterval })
    }

    query = {
      ...query,
      smooth: smoothInterval === SMOOTH_INTERVAL.RAW ? 'f' : 't',
      smoothInterval:
        smoothInterval !== SMOOTH_INTERVAL.RAW ? smoothInterval : undefined,
    }

    const qs = createQueryString(query)

    getPressureMonitorData(pillarId, qs)

    this.pollTimer = setInterval(this.poll, POLL_INTERVAL_MS)
  }

  componentWillUnmount() {
    clearInterval(this.pollTimer)
  }

  poll = () => {
    const { getPressureMonitorData, pillarId } = this.props
    const { startValue, endValue, smoothInterval } = this.state
    const durationSeconds = endValue.diff(startValue, 'seconds')
    const newEndValue = moment()
    const newStartValue = moment(newEndValue).subtract(
      durationSeconds,
      'seconds'
    )

    this.setState({ startValue: newStartValue, endValue: newEndValue })

    const query = {
      startTime: newStartValue.unix(),
      endTime: newEndValue.unix(),
      smooth: smoothInterval === SMOOTH_INTERVAL.RAW ? 'f' : 't',
      smoothInterval:
        smoothInterval !== SMOOTH_INTERVAL.RAW ? smoothInterval : undefined,
    }
    const qs = createQueryString(query)

    getPressureMonitorData(pillarId, qs)
  }

  onCloseDateRange = (startValue, endValue) => {
    const { getPressureMonitorData, pillarId, updateQueryParams } = this.props
    const { smoothInterval } = this.state

    if (!startValue?.isValid() || !endValue?.isValid()) {
      console.warn('Invalid date range values received:', {
        startValue,
        endValue,
      })
      return
    }

    const query = {
      startTime: startValue.unix(),
      endTime: endValue.unix(),
      smooth: smoothInterval === SMOOTH_INTERVAL.RAW ? 'f' : 't',
      smoothInterval:
        smoothInterval !== SMOOTH_INTERVAL.RAW ? smoothInterval : undefined,
    }

    this.setState({ startValue, endValue })
    const qs = createQueryString(query)

    updateQueryParams({
      pathname: global.location.pathname,
      search: qs,
    })
    getPressureMonitorData(pillarId, qs)
  }

  handleFilterChange = smoothInterval => {
    const { getPressureMonitorData, updateQueryParams, pillarId } = this.props
    const { startValue, endValue } = this.state

    this.setState({ smoothInterval })

    const query = {
      smooth: smoothInterval === SMOOTH_INTERVAL.RAW ? 'f' : 't',
      smoothInterval:
        smoothInterval !== SMOOTH_INTERVAL.RAW ? smoothInterval : undefined,
      startTime: startValue.unix(),
      endTime: endValue.unix(),
    }
    const qs = createQueryString(query)

    updateQueryParams({
      pathname: global.location.pathname,
      search: qs,
    })

    getPressureMonitorData(pillarId, qs)
  }

  render() {
    const { isLoading, pressureMonitorData } = this.props
    const { startValue, endValue, smoothInterval } = this.state
    const hasData = pressureMonitorData.data && pressureMonitorData.data.length

    return isLoading && !hasData ? (
      <Spin size="large" className="w-100 center mt5" />
    ) : (
      <Fragment>
        <H3>Historical Data</H3>
        <div className="mb2 flex-ns">
          <DateRange
            label="Select date range to view Pressure Monitor readings"
            startValue={startValue}
            endValue={endValue}
            onClose={this.onCloseDateRange}
            shouldLimitRange={!hasRole(ROLES.SUPER_ADMIN, ROLES.PILLAR_ADMIN)}
          />
          <Select
            className="ml4-ns"
            label="Smoothing Interval"
            input={{
              value: smoothInterval,
              onChange: x => this.handleFilterChange(x),
            }}
          >
            {Object.keys(SMOOTH_INTERVAL).map(x => (
              <Option value={SMOOTH_INTERVAL[x]} key={x}>
                {toTitleCase(x)}
              </Option>
            ))}
          </Select>
        </div>
        {hasData ? (
          <PressureMonitorChart
            data={pressureMonitorData.data}
            units={pressureMonitorData.units}
          />
        ) : (
          <H4 className="tc">No Data Available for Selected Date Range</H4>
        )}
      </Fragment>
    )
  }
}

export default PressureMonitorData
